import React from 'react';
import { ArrowRight, CheckCircle2, Users, MessageSquare, Zap, Target, TrendingUp, Calendar, Shield, Star, ChevronDown } from 'lucide-react';
import ApexNavbar from '../components/apex/ApexNavbar';
import ApexFooter from '../components/apex/ApexFooter';

const SKOOL_URL = 'https://www.skool.com/apex-mentorship-hub-7770/about';

const PTGrowthAcademy = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // What's included in the programme
  const programmeInclusions = [
    {
      icon: Target,
      title: 'Business Growth Frameworks',
      description: 'Step-by-step systems to attract clients, structure your offers and increase your revenue — whether you train online, in-person or hybrid.'
    },
    {
      icon: MessageSquare,
      title: 'Direct WhatsApp Access',
      description: 'Get direct access to me via WhatsApp. Ask questions, get feedback on your strategy and receive guidance when you need it — not just during scheduled calls.'
    },
    {
      icon: Zap,
      title: 'High-Converting Content Strategy',
      description: 'Learn how to create content that actually converts followers into paying clients — without pumping endless money into paid ads. Organic growth that works.'
    },
    {
      icon: Users,
      title: 'Private Skool Community',
      description: 'Join a community of like-minded personal trainers who are serious about building profitable businesses. Share wins, ask questions and learn from others.'
    },
    {
      icon: TrendingUp,
      title: 'Sales & Client Acquisition Systems',
      description: 'Proven frameworks for lead generation, sales conversations and client retention — without needing a massive following or expensive ad spend.'
    },
    {
      icon: Calendar,
      title: 'Weekly Implementation Support',
      description: 'Regular content, live sessions and actionable resources to keep you on track and implementing — not just consuming information.'
    }
  ];

  // Who this is for
  const idealMembers = [
    'Online personal trainers who want more clients and consistent income',
    'In-person PTs looking to add online revenue streams',
    'Hybrid coaches who want to scale without trading more time for money',
    'PTs earning under £5K/month who are ready to grow',
    'Trainers who are great at getting results but struggle with business',
    'Coaches who want mentorship without a £5,000 upfront investment'
  ];

  // Who this is NOT for
  const notFor = [
    'People looking for get-rich-quick schemes',
    'Trainers who are not willing to put in the work',
    'Anyone expecting results without implementation',
    'Coaches who just want to collect information without applying it'
  ];

  // My credentials
  const credentials = [
    { number: '10+', label: 'Years in Fitness Industry' },
    { number: '8,000+', label: 'Sales Consultations' },
    { number: '7', label: 'Figures in Fitness Sales' },
    { number: '100s', label: 'PTs & Gym Owners Helped' }
  ];

  // FAQ items
  const faqs = [
    {
      question: 'What exactly do I get for $167 per month?',
      answer: 'You get access to the complete Apex PT Growth Academy Skool community, all training resources and frameworks, weekly content and implementation support, and direct WhatsApp access to me for questions and guidance. Everything is designed to help you attract more clients and increase your revenue.'
    },
    {
      question: 'Is this just another online course?',
      answer: 'No. This is not a course you watch and forget. It is a practical mentorship programme with direct access to me, a supportive community, and ongoing implementation support. The focus is on doing, not just learning.'
    },
    {
      question: 'I am just starting out. Is this for me?',
      answer: 'If you are a qualified personal trainer who wants to build a real business, yes. You do not need an existing audience or years of experience. You just need to be willing to learn and implement.'
    },
    {
      question: 'What if I train clients in-person, not online?',
      answer: 'The strategies work for in-person, online and hybrid models. Many of the business fundamentals — sales, marketing, pricing, client retention — apply regardless of how you deliver your training.'
    },
    {
      question: 'How is this different from expensive mentorship programmes?',
      answer: 'Most high-ticket programmes charge £3,000 to £10,000 upfront for similar content. Apex PT Growth Academy gives you practical mentorship and direct support for $167 per month — no massive upfront investment required.'
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes. There is no long-term contract. You can cancel your membership at any time through Skool. I want you to stay because you are getting value, not because you are locked in.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <ApexNavbar />
      
      <main>
        {/* HERO SECTION */}
        <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          {/* Background image with premium overlay */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/pt-hero.webp)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/90 via-[#0A0A0A]/80 to-[#0A0A0A]/95" />
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-32 md:py-40">
            <div className="text-center space-y-8">
              {/* Minimal badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#ffffff15]">
                <span className="w-1.5 h-1.5 bg-[#22C55E] rounded-full" />
                <span className="text-[#71717A] text-xs font-medium tracking-widest uppercase">
                  PT Growth Academy
                </span>
              </div>

              {/* Clean headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
                Build Your PT Business to
                <br />
                <span className="text-[#22C55E]">£10K+ Per Month.</span>
              </h1>

              {/* Minimal subheadline */}
              <p className="text-lg md:text-xl text-[#71717A] leading-relaxed max-w-2xl mx-auto">
                Systems, strategies and direct mentorship to grow your personal training business — without a massive following or £5,000 mentorship.
              </p>

              {/* CTAs - cleaner */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a
                  href={SKOOL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-join-cta"
                  className="group inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#1DB954] text-black font-semibold rounded-full px-8 py-4 transition-all duration-300"
                >
                  Join Now — $167/month
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <button
                  onClick={() => scrollToSection('whats-included')}
                  data-testid="hero-see-more"
                  className="inline-flex items-center justify-center gap-2 text-[#71717A] hover:text-white font-medium rounded-full px-8 py-4 transition-colors duration-300"
                >
                  See What&apos;s Included
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              {/* Minimal note */}
              <p className="text-[#52525B] text-sm">
                Cancel anytime. No long-term contract.
              </p>
            </div>
          </div>
        </section>

        {/* CREDIBILITY SECTION */}
        <section className="relative py-12 border-b border-[#ffffff08]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              {credentials.map((cred) => (
                <div key={cred.label} className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-white">{cred.number}</p>
                  <p className="text-xs text-[#52525B] uppercase tracking-wider mt-1">{cred.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* THE PROBLEM SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                YOU ARE GREAT AT TRAINING CLIENTS.
                <br />
                <span className="text-[#22C55E]">BUT NOBODY TAUGHT YOU HOW TO RUN A BUSINESS.</span>
              </h2>
            </div>

            <div className="space-y-6 text-lg text-[#A1A1AA] leading-relaxed">
              <p>
                You got qualified. You learned the science. You know how to get results for clients.
              </p>
              <p>
                But when it comes to actually building a business — attracting clients, pricing your services, selling without feeling salesy, creating consistent income — you are mostly figuring it out as you go.
              </p>
              <p>
                You have probably seen coaches charging £5,000 or £10,000 for mentorship programmes. Maybe you have been tempted. Maybe you have even signed up and felt burned.
              </p>
              <p className="text-white font-semibold">
                You do not need another overpriced course full of theory. You need practical systems from someone who has actually done this — and direct support when you get stuck.
              </p>
            </div>
          </div>
        </section>

        {/* WHAT'S INCLUDED SECTION */}
        <section id="whats-included" className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                WHAT YOU GET INSIDE{' '}
                <span className="text-[#22C55E]">APEX PT GROWTH ACADEMY</span>
              </h2>
              <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
                Everything you need to build your PT business towards £10K+ per month
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {programmeInclusions.map((item) => (
                <div
                  key={item.title}
                  className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 hover:border-[#22C55E]/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#22C55E]/10 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-[#22C55E]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-[#A1A1AA] leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>

            {/* WhatsApp highlight */}
            <div className="mt-12 bg-gradient-to-r from-[#121212] via-[#0d1f12] to-[#121212] border border-[#22C55E]/30 rounded-2xl p-8 md:p-10">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-16 h-16 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-8 h-8 text-[#22C55E]" />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                    DIRECT WHATSAPP ACCESS — NOT JUST ANOTHER GROUP CHAT
                  </h3>
                  <p className="text-[#A1A1AA] leading-relaxed">
                    Most programmes give you a community and leave you to figure it out. Apex PT Growth Academy includes{' '}
                    <span className="text-white font-semibold">direct WhatsApp access to me</span>. 
                    Ask questions, get feedback on your strategy, troubleshoot problems — real support when you need it.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHO I AM SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#22C55E]/30 bg-[#22C55E]/10 mb-6">
                  <span className="text-[#22C55E] text-xs font-bold tracking-wider uppercase">
                    Your Mentor
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  I DO NOT JUST TEACH THIS STUFF.{' '}
                  <span className="text-[#22C55E]">I HAVE ACTUALLY DONE IT.</span>
                </h2>
                <div className="space-y-4 text-[#A1A1AA] leading-relaxed">
                  <p>
                    I have spent over a decade in the fitness industry — not just coaching coaches, but actually doing the work.
                  </p>
                  <p>
                    I have run gyms. I have managed sales teams. I have conducted over 8,000 consultations and generated seven figures in fitness sales.
                  </p>
                  <p>
                    I have built sales processes, acquired clients, developed systems and helped hundreds of gym owners and personal trainers grow their businesses.
                  </p>
                  <p className="text-white font-semibold">
                    Apex PT Growth Academy takes that hands-on experience and puts it directly into your business — without the £5,000+ price tag of most mentorship programmes.
                  </p>
                </div>
              </div>
              <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-6">What You Are Getting Access To:</h3>
                <ul className="space-y-4">
                  {[
                    'Real-world experience running fitness businesses',
                    'Practical systems that actually work',
                    'Direct access via WhatsApp — not a chatbot',
                    'Guidance from someone who has done the hard yards',
                    'Honest, no-BS advice based on results'
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#22C55E] mt-0.5 flex-shrink-0" />
                      <span className="text-[#A1A1AA]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* WHO THIS IS FOR SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                IS APEX PT GROWTH ACADEMY{' '}
                <span className="text-[#22C55E]">RIGHT FOR YOU?</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* This IS for you */}
              <div className="bg-[#121212] border border-[#22C55E]/30 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-[#22C55E]/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                  </div>
                  <h3 className="text-xl font-bold text-white">This IS For You If...</h3>
                </div>
                <ul className="space-y-3">
                  {idealMembers.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="text-[#22C55E] mt-1">•</span>
                      <span className="text-[#A1A1AA]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* This is NOT for you */}
              <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <span className="text-red-500 text-xl font-bold">✕</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">This is NOT For You If...</h3>
                </div>
                <ul className="space-y-3">
                  {notFor.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="text-red-500 mt-1">•</span>
                      <span className="text-[#A1A1AA]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* PRICING SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                REAL MENTORSHIP.{' '}
                <span className="text-[#22C55E]">WITHOUT THE £5,000 PRICE TAG.</span>
              </h2>
            </div>

            <div className="bg-[#121212] border-2 border-[#22C55E] rounded-2xl p-8 md:p-12">
              <div className="text-center mb-8">
                <p className="text-[#A1A1AA] text-lg mb-2">Apex PT Growth Academy</p>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl md:text-6xl font-bold text-white">$167</span>
                  <span className="text-[#A1A1AA] text-lg">/month</span>
                </div>
                <p className="text-[#71717A] text-sm mt-2">Cancel anytime. No long-term contract.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  'Business growth frameworks & systems',
                  'Direct WhatsApp access to me',
                  'High-converting content strategies',
                  'Private Skool community',
                  'Client acquisition without paid ads',
                  'Weekly implementation support'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#22C55E] flex-shrink-0" />
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <a
                  href={SKOOL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pricing-join-cta"
                  className="group inline-flex items-center justify-center gap-3 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-10 py-5 text-lg transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-[#22C55E]/25"
                >
                  Join Apex PT Growth Academy
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>

            {/* Comparison */}
            <div className="mt-12 text-center">
              <p className="text-[#A1A1AA] mb-6">Compare that to...</p>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { label: 'Average PT Mentorship', price: '£3,000 - £10,000', note: 'Upfront' },
                  { label: 'Group Coaching Programmes', price: '£2,000 - £5,000', note: 'Per programme' },
                  { label: 'Apex PT Growth Academy', price: '$167/month', note: 'Cancel anytime', highlight: true }
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`p-6 rounded-xl ${
                      item.highlight
                        ? 'bg-[#22C55E]/10 border border-[#22C55E]/30'
                        : 'bg-[#121212] border border-[#27272A]'
                    }`}
                  >
                    <p className="text-sm text-[#71717A] mb-2">{item.label}</p>
                    <p className={`text-xl font-bold ${item.highlight ? 'text-[#22C55E]' : 'text-white'}`}>
                      {item.price}
                    </p>
                    <p className="text-xs text-[#71717A] mt-1">{item.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                FREQUENTLY ASKED{' '}
                <span className="text-[#22C55E]">QUESTIONS</span>
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="group bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <ChevronDown className="w-5 h-5 text-[#22C55E] transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-6 pb-6">
                    <p className="text-[#A1A1AA] leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f12]/50 to-transparent" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              READY TO BUILD A PT BUSINESS THAT{' '}
              <span className="text-[#22C55E]">ACTUALLY PAYS YOU?</span>
            </h2>
            <p className="text-lg text-[#A1A1AA] mb-4 max-w-2xl mx-auto">
              Stop figuring it out alone. Get the systems, strategies and direct mentorship to grow your business towards £10K+ per month.
            </p>
            <p className="text-[#22C55E] font-semibold mb-10">
              Join Apex PT Growth Academy today — $167/month. Cancel anytime.
            </p>
            <a
              href={SKOOL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-join-cta"
              className="group inline-flex items-center justify-center gap-3 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold uppercase tracking-wide rounded-full px-10 py-5 text-lg transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-[#22C55E]/25"
            >
              Join Apex PT Growth Academy
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="text-[#71717A] text-sm mt-6">
              No £5,000 upfront. No long-term contract. Just practical business growth.
            </p>
          </div>
        </section>
      </main>

      <ApexFooter />
    </div>
  );
};

export default PTGrowthAcademy;
